import React from 'react';
import { View } from 'react-native';

import { Button } from '@/components/ui';
import {
	APPLICATION_CLOSED_STATUSES,
	APPLICATION_DECISION_STATUSES,
	APPLICATION_STATUS_TRANSITIONS,
	applicationStatusLabel,
	ExteriorApplicationStatus,
	isApplicationOpen,
} from '@/utils/application-status';

export interface StatusButtonsProps {
	currentStatus: number;
	/** Fired for a plain move. */
	onTransition: (next: ExteriorApplicationStatus) => void;
	/** Fired for Approved/Rejected/Withdrawn, which need the closing dialog. */
	onRequestDecision: (next: ExteriorApplicationStatus) => void;
	/** Fired to move a CLOSED request back to its reopen target — undoing the decision or withdrawal. */
	onRequestReopen: () => void;
	canLogDecision: boolean;
	isSubmitting?: boolean;
}

/**
 * The next legal moves, straight from the transition table (#369).
 *
 * Rendered FROM the table rather than hardcoded, so the UI cannot offer a move the API
 * would reject — the server checks the same table, and the two disagreeing is how you get
 * a button that 409s.
 *
 * Closing a request (Approved / Rejected / Withdrawn) opens a dialog instead of firing
 * directly: it stamps who closed it and when.
 *
 * The one way out of a closed state is a REOPEN, not a plain move: it clears that stamp, only
 * a decider may do it, and it must not reach `onTransition`, which the detail page routes to
 * the board-approval letter for Waiting for Board Approval.
 */
export const StatusTransitionButtons: React.FC<StatusButtonsProps> = ({
	currentStatus,
	onTransition,
	onRequestDecision,
	onRequestReopen,
	canLogDecision,
	isSubmitting = false,
}) => {
	const next = APPLICATION_STATUS_TRANSITIONS[currentStatus as ExteriorApplicationStatus] ?? [];
	if (next.length === 0) return null;
	const isReopen = !isApplicationOpen(currentStatus);

	return (
		<View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 6 }}>
			{next.map((status) => {
				const isClosing = APPLICATION_CLOSED_STATUSES.includes(status);
				const isDecision = APPLICATION_DECISION_STATUSES.includes(status);
				// Hidden rather than disabled for a role that can't decide: a permanently
				// greyed button is just noise to someone who will never be able to use it.
				if ((isClosing || isReopen) && !canLogDecision) return null;
				if (isReopen) {
					return (
						<Button
							key={status}
							mode="outlined"
							icon="arrow-undo-outline"
							onPress={onRequestReopen}
							disabled={isSubmitting}
							testID={`btn-status-${status}`}
						>
							{`Reopen — back to ${applicationStatusLabel(status)}`}
						</Button>
					);
				}
				return (
					<Button
						key={status}
						mode={isDecision ? 'contained' : 'outlined'}
						variant={status === ExteriorApplicationStatus.Rejected ? 'error' : undefined}
						icon={status === ExteriorApplicationStatus.Withdrawn ? 'archive-outline' : undefined}
						onPress={() => (isClosing ? onRequestDecision(status) : onTransition(status))}
						disabled={isSubmitting}
						testID={`btn-status-${status}`}
					>
						{applicationStatusLabel(status)}
					</Button>
				);
			})}
		</View>
	);
};

export default StatusTransitionButtons;
